// FudiTap guest order contract v1 (nfc-panel docs/architecture/FUDITAP_GUEST_ORDER_CONTRACT.md).
//
// The PWA sends ONLY { cardCode | tableToken, idempotencyKey, items:[{productId, quantity, optionIds}], note }.
// Prices, totals, currency and table are decided by the server. One idempotency
// key is kept per cart content until a definitive answer arrives, so a retry
// after a network failure can never create a second order.
import { entryBody, entryFrom, entryQuery } from "@/lib/guestEntry";

export const ORDER_LIMITS = { maxLines: 50, maxQuantity: 20, maxNoteChars: 200, maxOptions: 20 };
export const ORDER_ATTEMPT_KEY = "fuditap_order_attempt";
const STATUSES = ["pending", "accepted", "preparing", "ready", "served", "completed", "cancelled"];
// Backend status -> existing PWA timeline status.
const PWA_STATUS = {
  pending: "placed", accepted: "placed", preparing: "preparing", ready: "ready",
  served: "serving", completed: "completed", cancelled: "canceled",
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
const chars = (s) => [...s].length;
const isInt = (v, min, max) => typeof v === "number" && Number.isInteger(v) && v >= min && v <= max;
const isObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);

/** RFC 4122 v4 from the platform CSPRNG; no Math.random fallback. */
export function newIdempotencyKey(cryptoImpl = globalThis.crypto) {
  if (typeof cryptoImpl?.randomUUID === "function") return cryptoImpl.randomUUID();
  if (typeof cryptoImpl?.getRandomValues !== "function") throw new Error("NO_SECURE_RANDOM");
  const b = cryptoImpl.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export class GuestOrderInputError extends Error {
  constructor(reason) {
    super(reason);
    this.name = "GuestOrderInputError";
    this.reason = reason; // "empty" | "too_many_lines" | "quantity" | "note_too_long" | "option_invalid"
  }
}

// Selected option ids of one cart line (selections: { groupId: [optionId] }), sorted.
function lineOptionIds(line) {
  const ids = Object.values(line.selections || {}).flat();
  if (ids.length > ORDER_LIMITS.maxOptions || !ids.every((id) => typeof id === "string" && UUID.test(id)) ||
      new Set(ids.map((id) => id.toLowerCase())).size !== ids.length) throw new GuestOrderInputError("option_invalid");
  return ids.map((id) => id.toLowerCase()).sort();
}

/**
 * Cart lines -> request items (productId + quantity + selected optionIds; lines
 * with the same product and option set merged) and the order note composed from
 * per-line notes ("Name: note; ..."). Option prices are never sent.
 */
export function buildOrderPayload(lines) {
  const quantities = new Map();
  const notes = [];
  for (const line of lines || []) {
    if (line.isCombo || typeof line.productId !== "string") continue; // no combos in the real menu
    const lineKey = JSON.stringify([line.productId, lineOptionIds(line)]);
    quantities.set(lineKey, (quantities.get(lineKey) || 0) + line.quantity);
    const note = typeof line.note === "string" ? line.note.trim() : "";
    if (note) notes.push(`${typeof line.product?.name === "string" ? line.product.name : ""}: ${note}`.replace(/^: /, ""));
  }
  if (quantities.size === 0) throw new GuestOrderInputError("empty");
  if (quantities.size > ORDER_LIMITS.maxLines) throw new GuestOrderInputError("too_many_lines");
  const items = [...quantities].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)).map(([lineKey, quantity]) => {
    if (!isInt(quantity, 1, ORDER_LIMITS.maxQuantity)) throw new GuestOrderInputError("quantity");
    const [productId, optionIds] = JSON.parse(lineKey);
    return { productId, quantity, optionIds };
  });
  const note = notes.length ? notes.join("; ") : null;
  if (note && chars(note) > ORDER_LIMITS.maxNoteChars) throw new GuestOrderInputError("note_too_long");
  return { items, note };
}

function readAttempt(storage) {
  try { return JSON.parse(storage?.getItem(ORDER_ATTEMPT_KEY) || "null"); } catch { return null; }
}
function writeAttempt(storage, value) {
  try {
    if (value) storage?.setItem(ORDER_ATTEMPT_KEY, JSON.stringify(value));
    else storage?.removeItem(ORDER_ATTEMPT_KEY);
  } catch { /* in-memory only: a reload then starts a fresh attempt */ }
}

/** Same card + same items + same note => same key until a definitive answer. */
export function idempotencyKeyFor(cardCode, payload, storage, newKey) {
  const fingerprint = JSON.stringify({ cardCode, items: payload.items, note: payload.note });
  const prev = readAttempt(storage);
  if (prev && prev.fingerprint === fingerprint && typeof prev.key === "string" && UUID.test(prev.key)) return prev.key;
  const key = newKey();
  writeAttempt(storage, { fingerprint, key });
  return key;
}

/** Strict rebuild of the server reply (unknown fields are dropped). */
export function parseOrder(json) {
  if (!isObject(json) || typeof json.orderToken !== "string" || !UUID.test(json.orderToken) ||
      !STATUSES.includes(json.status) || json.currency !== "TRY" ||
      !isInt(json.totalMinor, 0, Number.MAX_SAFE_INTEGER) || !Array.isArray(json.items) ||
      json.items.length < 1 || json.items.length > ORDER_LIMITS.maxLines ||
      !(json.note === null || (typeof json.note === "string" && chars(json.note) <= ORDER_LIMITS.maxNoteChars)) ||
      typeof json.createdAt !== "string" || !ISO.test(json.createdAt) ||
      typeof json.updatedAt !== "string" || !ISO.test(json.updatedAt)) return null;
  let sum = 0;
  const seen = new Set();
  const items = [];
  const isName = (v) => typeof v === "string" && !!v.trim() && chars(v) <= 200;
  for (const i of json.items) {
    if (!isObject(i) || !isInt(i.lineNo, 1, ORDER_LIMITS.maxLines) || seen.has(i.lineNo) ||
        typeof i.productId !== "string" || !UUID.test(i.productId) || !isName(i.name) ||
        !isInt(i.quantity, 1, ORDER_LIMITS.maxQuantity) || !isInt(i.basePriceMinor, 0, 2147483647) ||
        !isInt(i.unitPriceMinor, 0, 2147483647) || !Array.isArray(i.options) ||
        i.options.length > ORDER_LIMITS.maxOptions || i.lineTotalMinor !== i.unitPriceMinor * i.quantity) return null;
    seen.add(i.lineNo);
    const optionSeen = new Set();
    let deltas = 0;
    const options = [];
    for (const o of i.options) {
      if (!isObject(o) || typeof o.optionId !== "string" || !UUID.test(o.optionId) || optionSeen.has(o.optionId) ||
          !isName(o.groupName) || !isName(o.name) || !isInt(o.priceDeltaMinor, -1000000, 1000000)) return null;
      optionSeen.add(o.optionId);
      deltas += o.priceDeltaMinor;
      options.push({ optionId: o.optionId, groupName: o.groupName, name: o.name, priceDeltaMinor: o.priceDeltaMinor });
    }
    if (i.unitPriceMinor !== i.basePriceMinor + deltas) return null;
    sum += i.lineTotalMinor;
    items.push({ lineNo: i.lineNo, productId: i.productId, name: i.name, quantity: i.quantity,
      basePriceMinor: i.basePriceMinor, unitPriceMinor: i.unitPriceMinor, lineTotalMinor: i.lineTotalMinor, options });
  }
  if (sum !== json.totalMinor) return null;
  return { orderToken: json.orderToken.toLowerCase(), status: json.status, currency: "TRY",
    totalMinor: json.totalMinor, note: json.note, items, createdAt: json.createdAt, updatedAt: json.updatedAt };
}

const REASONS = {
  ORDER_INVALID: "invalid", ORDER_TOO_LARGE: "invalid", MENU_NOT_FOUND: "not_found", TABLE_REQUIRED: "table_required",
  PRODUCT_UNAVAILABLE: "product_unavailable", IDEMPOTENCY_KEY_REUSED: "conflict", RATE_LIMITED: "rate_limited",
  OPTION_INVALID: "option_invalid", TABLE_SESSION_REQUIRED: "table_session_required",
};

async function request(url, init, fetchImpl, timeoutMs) {
  const controller = typeof AbortController === "function" ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    return await fetchImpl(url, { ...init, credentials: "omit", cache: "no-store", redirect: "error",
      referrerPolicy: "no-referrer", signal: controller?.signal });
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * POST the cart. Returns { ok: true, order } or { ok: false, reason }.
 * The idempotency key is kept on every outcome that might have created (or may
 * still create) the order, and on failures that need the same request again;
 * it is cleared only after a definitive success or a key conflict.
 */
export async function submitGuestOrder({ cardCode, tableToken, lines }, {
  baseUrl = "", fetchImpl = globalThis.fetch, storage, newKey, timeoutMs = 15000,
} = {}) {
  const entry = entryFrom(tableToken === undefined ? { cardCode } : cardCode === undefined ? { tableToken } : null);
  if (!entry) return { ok: false, reason: "not_found" };
  const code = entry.cardCode ?? `table:${entry.tableToken}`;   // idempotency fingerprint scope only
  let payload;
  try { payload = buildOrderPayload(lines); } catch (e) { return { ok: false, reason: e.reason || "invalid" }; }
  let idempotencyKey;
  try {
    idempotencyKey = idempotencyKeyFor(code, payload, storage, newKey || (() => newIdempotencyKey()));
  } catch {
    return { ok: false, reason: "unavailable" }; // no secure randomness: never send a weak key
  }
  const body = { ...entryBody(entry), idempotencyKey, items: payload.items, ...(payload.note ? { note: payload.note } : {}) };
  let response;
  try {
    response = await request(`${baseUrl}/api/guest/orders`, {
      method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify(body),
    }, fetchImpl, timeoutMs);
  } catch {
    return { ok: false, reason: "network" }; // the order may exist: keep the key, retry the same request
  }
  let json = null;
  try { json = await response.json(); } catch { /* handled below */ }
  if (response.ok) {
    const order = parseOrder(json);
    if (!order) return { ok: false, reason: "unavailable" };
    writeAttempt(storage, null);
    return { ok: true, order };
  }
  const reason = REASONS[json?.error] || "unavailable";
  if (reason === "conflict") writeAttempt(storage, null);
  return { ok: false, reason };
}

/** Status of one order (card or table entry + token). Returns { ok, order } or { ok: false, reason }. */
export async function fetchGuestOrderStatus(entryOrCode, orderToken, {
  baseUrl = "", fetchImpl = globalThis.fetch, timeoutMs = 10000,
} = {}) {
  const entry = entryFrom(typeof entryOrCode === "string" ? { cardCode: entryOrCode } : entryOrCode);
  if (!entry || typeof orderToken !== "string" || !UUID.test(orderToken)) return { ok: false, reason: "not_found" };
  let response;
  try {
    response = await request(`${baseUrl}/api/guest/orders/status?${entryQuery(entry)}&order=${encodeURIComponent(orderToken)}`,
      { method: "GET", headers: { Accept: "application/json" } }, fetchImpl, timeoutMs);
  } catch {
    return { ok: false, reason: "network" };
  }
  if (response.status === 404) return { ok: false, reason: "not_found" };
  if (!response.ok) return { ok: false, reason: "unavailable" };
  let json = null;
  try { json = await response.json(); } catch { /* handled below */ }
  const order = parseOrder(json);
  return order ? { ok: true, order } : { ok: false, reason: "unavailable" };
}

export const toPwaStatus = (status) => PWA_STATUS[status] || "placed";

/** Server order -> existing PWA order record (display values only; server stays authoritative). */
export function toLocalOrder(order, { sessionId, restaurantId, branchId }) {
  const placedAt = Date.parse(order.createdAt);
  const status = toPwaStatus(order.status);
  return {
    id: `order_${order.orderToken}`,
    remoteToken: order.orderToken,
    remoteStatus: order.status,
    number: order.orderToken.replace(/-/g, "").slice(0, 6).toUpperCase(),
    restaurantId, branchId,
    tableNumber: null, // no table number is invented
    sessionId,
    staffId: null,
    items: order.items.map((i) => ({
      lineId: `line_${i.lineNo}`, productId: i.productId, isCombo: false, name: i.name, quantity: i.quantity,
      selections: {}, options: i.options.map((o) => ({ name: o.name, groupName: o.groupName })),
      unitPrice: i.unitPriceMinor / 100, lineTotal: i.lineTotalMinor / 100,
    })),
    total: order.totalMinor / 100,
    totalMinor: order.totalMinor,
    note: order.note,
    status,
    placedAt,
    estReadyMin: null,
    statusHistory: [{ status, at: placedAt }],
  };
}
